import { useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BookOpen, Clock3, Flame, Send, AlertTriangle, UploadCloud, X, File as FileIcon, Image as ImageIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ticketsApi } from '@/api/endpoints';
import { Button, Card } from '../components/ui';
import {
  categories,
  type DesignSeverity,
  type DesignTicketType,
  unmapCategory,
  unmapPriority,
} from '../lib/ticketMap';

const frequentProblems: Array<{ title: string; tag: string; type: DesignTicketType }> = [
  { title: 'Клиент не запускается после обновления', tag: 'Не запускается', type: 'incident' },
  { title: 'Ошибка активации лицензии Windows', tag: 'Не запускается', type: 'incident' },
  { title: 'Нет связи ПК с панелью (NATS)', tag: 'Ошибка / баг', type: 'problem' },
  { title: 'Двойное списание с баланса', tag: 'Биллинг', type: 'billing' },
  { title: 'Шелл зависает при выходе из игры', tag: 'Ошибка / баг', type: 'problem' },
  { title: 'Как привязать новый ПК к клубу', tag: 'Как сделать', type: 'how_to' },
];

const severityOptions: Array<{
  id: Extract<DesignSeverity, 'S1' | 'S2' | 'S3'>;
  label: string;
  description: string;
  tone: 'normal' | 'high' | 'critical';
}> = [
  { id: 'S3', label: 'Обычная', description: 'Обычный запрос без срочности', tone: 'normal' },
  { id: 'S2', label: 'Высокая', description: 'Требует повышенного внимания', tone: 'high' },
  { id: 'S1', label: 'Критическая', description: 'Серьёзная проблема, блокирует работу', tone: 'critical' },
];

function isValidType(v: string | null): v is DesignTicketType {
  return v === 'incident' || v === 'problem' || v === 'billing' || v === 'how_to' || v === 'service_request';
}

export function CreateTicketView() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const presetType = params.get('type');
  const initialType: DesignTicketType = isValidType(presetType) ? presetType : 'problem';

  const [description, setDescription] = useState('');
  const [type, setType] = useState<DesignTicketType>(initialType);
  const [severity, setSeverity] = useState<DesignSeverity>('S3');
  // Файлы, выбранные ДО создания тикета. После создания зальём каждый
  // через ticketsApi.uploadAttachment(ticketId, file). Лимиты как на
  // admin-app / других формах: 25 MB / шт, до 10 файлов на обращение.
  const [attachments, setAttachments] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_MB = 25;
  const MAX_FILES = 10;

  const acceptFiles = (files: File[]) => {
    if (!files.length) return;
    const tooBig: string[] = [];
    const filtered = files.filter((f) => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        tooBig.push(f.name);
        return false;
      }
      return true;
    });
    if (tooBig.length) toast.error(`Файл больше ${MAX_FILE_MB} МБ: ${tooBig.join(', ')}`);
    setAttachments((cur) => {
      const merged = [...cur, ...filtered];
      if (merged.length > MAX_FILES) {
        toast.error(`Не больше ${MAX_FILES} файлов на обращение — лишние отброшены`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };
  const removeFile = (idx: number) =>
    setAttachments((cur) => cur.filter((_, i) => i !== idx));

  // Backend требует subject >= 3 символов (первая строка description, до 90),
  // description >= 1. Даём отправить как только описание не пустое —
  // никаких «Минимум 15 символов».
  const trimmed = description.trim();
  const isValid = trimmed.length >= 3;

  const createMutation = useMutation({
    mutationFn: async () => {
      const subject = description.trim().split('\n')[0].slice(0, 90);
      const ticket = await ticketsApi.create({
        subject,
        description: description.trim(),
        category: unmapCategory(type),
        priority: unmapPriority(severity),
      });
      // Заливаем вложения последовательно — если один упадёт, остальные
      // остаются попыткой пойти дальше (тикет уже создан, не критично).
      for (const file of attachments) {
        try {
          await ticketsApi.uploadAttachment(ticket.id, file);
        } catch (err) {
          toast.error(`Не удалось прикрепить: ${file.name}`);
        }
      }
      return ticket;
    },
    onSuccess: (ticket) => {
      toast.success('Обращение создано');
      qc.invalidateQueries({ queryKey: ['home-tickets'] });
      qc.invalidateQueries({ queryKey: ['tickets-list'] });
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Не удалось создать обращение';
      toast.error(msg);
    },
  });

  const submit = () => {
    if (!isValid || createMutation.isPending) return;
    createMutation.mutate();
  };

  return (
    <div className="view active">
      <div className="page-hero">
        <h2>Создать обращение</h2>
        <p>Опишите проблему своими словами — служба поддержки Club:Core ответит вам прямо в переписке по обращению.</p>
      </div>
      <div className="create-reference-layout">
        <Card className="create-reference-card">
          <div className="form-section reference-section">
            <span className="secnum">01 · Что случилось</span>
            <label className="field problem-field">
              <span className="sr-only">Опишите проблему</span>
              <textarea
                className="textarea reference-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Что произошло, когда это началось, какие шаги к этому привели и что вы уже пробовали..."
              />
            </label>
            <p className="form-hint">Чем подробнее опишете — тем быстрее мы разберёмся. Тему обращения сформируем автоматически.</p>
            {description.length > 0 && !isValid ? <div className="form-error">Опишите проблему хотя бы в трёх символах</div> : null}
          </div>
          <div className="form-section reference-section">
            <span className="secnum">02 · категория и срочность</span>
            <div className="form-label required">Категория</div>
            <div className="cat-pick reference-cat-pick">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    type="button"
                    aria-label={category.label}
                    className={`cat-pick-card reference-cat-card ${type === category.id ? 'selected' : ''} ${
                      category.id === 'service_request' ? 'centered' : ''
                    }`}
                    key={category.id}
                    onClick={() => setType(category.id)}
                  >
                    <span className="mini-icon compact">
                      <Icon size={18} />
                    </span>
                    <span>
                      <strong>{category.label}</strong>
                      <em>{category.description}</em>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="form-label">Срочность</div>
            <div className="severity-row reference-severity-row">
              {severityOptions.map((item) => {
                const isCritical = item.tone === 'critical';
                return (
                  <button
                    type="button"
                    className={`severity reference-severity severity-${item.tone} ${severity === item.id ? 'selected' : ''}`}
                    key={item.id}
                    onClick={() => setSeverity(item.id)}
                  >
                    {isCritical ? <AlertTriangle className="severity-alert-icon" size={14} /> : <span className={`severity-dot severity-dot-${item.tone}`} />}
                    <span>
                      <strong>{item.label}</strong>
                      <em>{item.description}</em>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="form-section reference-section attachments-section">
            <span className="secnum attachments-secnum">
              03 · Вложения
              <em>Скриншоты и логи ускорят разбор ({attachments.length}/{MAX_FILES}, до {MAX_FILE_MB} МБ файл)</em>
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) acceptFiles(Array.from(e.target.files));
                e.target.value = '';
              }}
            />
            <button
              className="dropzone reference-dropzone"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onPaste={(e) => {
                const items = e.clipboardData?.items || [];
                const files: File[] = [];
                for (const it of Array.from(items)) {
                  if (it.kind === 'file') {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                  }
                }
                if (files.length) {
                  e.preventDefault();
                  acceptFiles(files);
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer?.files) acceptFiles(Array.from(e.dataTransfer.files));
              }}
              style={{
                background: dragOver ? 'rgba(91,124,255,0.10)' : undefined,
                borderColor: dragOver ? 'rgba(91,124,255,0.55)' : undefined,
              }}
            >
              <UploadCloud size={23} />
              <span>Перетащите файлы, нажмите чтобы выбрать, или вставьте Ctrl+V из буфера</span>
            </button>
            {attachments.length > 0 ? (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {attachments.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      background: 'var(--fill-2, rgba(255,255,255,0.04))',
                      border: '1px solid var(--line, rgba(255,255,255,0.1))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    {f.type.startsWith('image/') ? (
                      <ImageIcon size={14} style={{ color: '#a5b4ff' }} />
                    ) : (
                      <FileIcon size={14} style={{ color: '#727b96' }} />
                    )}
                    <span style={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.name}
                    </span>
                    <span style={{ color: 'var(--muted, #727b96)', fontSize: 11 }}>
                      {(f.size / 1024).toFixed(0)} КБ
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      aria-label="Удалить"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--muted, #727b96)',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'inline-flex',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="create-form-footer">
            <span className="first-response-note">
              <Clock3 size={15} />
              Обычно первый ответ — в пределах SLA категории
            </span>
            <Button
              className="btn-primary create-submit"
              onClick={submit}
              disabled={!isValid || createMutation.isPending}
              aria-busy={createMutation.isPending || undefined}
            >
              <Send size={17} />
              {createMutation.isPending ? 'Отправка…' : 'Отправить обращение'}
            </Button>
          </div>
        </Card>
        <aside className="create-support-column">
          <Card className="frequent-card">
            <div className="card-head reference-card-head">
              <Flame size={18} />
              <h3>Частые проблемы</h3>
            </div>
            <div className="frequent-list">
              {frequentProblems.map((problem) => {
                const category = categories.find((c) => c.id === problem.type) ?? categories[0];
                const Icon = category.icon;
                return (
                  <button
                    key={problem.title}
                    className="quick-issue reference-quick-issue"
                    type="button"
                    onClick={() => {
                      setType(problem.type);
                      setDescription(`${problem.title}. `);
                    }}
                  >
                    <span className="mini-icon compact">
                      <Icon size={17} />
                    </span>
                    <span>
                      <strong>{problem.title}</strong>
                      <em>{problem.tag}</em>
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
          <Card className="kb-suggestion-card">
            <span className="mini-icon compact">
              <BookOpen size={18} />
            </span>
            <span>
              <strong>Сначала загляните в базу знаний</strong>
              <em>Возможно, ответ уже готов</em>
            </span>
            <Button className="btn-ghost btn-sm" onClick={() => navigate('/knowledge')}>
              Открыть
            </Button>
          </Card>
        </aside>
      </div>
    </div>
  );
}
